import type { QueryClient } from '@tanstack/react-query'
import {
  HeadContent,
  Outlet,
  createRootRouteWithContext,
  redirect,
} from '@tanstack/react-router'
import { Providers } from '@/components/providers'
import { currentUser, isAuthPath, isPublicPath } from '@/lib/auth/session'

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  /**
   * The auth gate, and the whole of what `src/proxy.ts` used to do.
   *
   * That file was Next middleware, and it carried a second job this does not
   * need: refreshing the Supabase session on every request, because
   * `@supabase/ssr` hands back rotated tokens through `setAll` and a Server
   * Component cannot write cookies. Without it, a token that expired while a
   * tab was backgrounded was never renewed and the next render 401'd.
   *
   * There is no server here, so that job belongs to nobody. supabase-js
   * refreshes on a timer in the browser, independent of navigation — which is
   * strictly better for the case that motivated the middleware, since a
   * backgrounded tab is precisely the one that does not navigate.
   */
  beforeLoad: async ({ location }) => {
    const user = await currentUser()
    const { pathname, searchStr } = location
    const publicPath = isPublicPath(pathname)

    if (!user && !publicPath) {
      throw redirect({
        to: '/login',
        /* Carry where they were headed so sign-in can land them there rather
           than dumping everyone on the default organization. A deep link to a
           specific QR code is exactly when someone is most likely to hit the
           login wall. */
        search: pathname === '/' ? {} : { next: `${pathname}${searchStr}` },
      })
    }

    /* Only the sign-in-shaped screens, not every public one: `/invitations/:id`
       is public AND meaningful to a signed-in visitor, and bouncing them here
       would silently discard the invitation they followed. */
    if (user && isAuthPath(pathname)) {
      /* `/` resolves the organization. It is the only place that can: the slug
         lives in a table, and until the session query lands nothing here knows
         which workspace this account owns. */
      throw redirect({ to: '/' })
    }

    return { user }
  },

  /* The document title, for routes that do not set their own. `index.html`
     carries the same string so the tab is right before any JavaScript runs;
     this is what restores it after a route that overrode it. */
  head: () => ({ meta: [{ title: 'Constellation QR' }] }),

  component: RootLayout,
})

function RootLayout() {
  return (
    <Providers>
      {/* Applies whichever `head` the deepest matched route contributed. */}
      <HeadContent />
      <Outlet />
    </Providers>
  )
}
